import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  DialogContent,
  List,
  ListItem,
  Paper,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import { TextField } from 'material-ui'
import TooltipModal from '../../../../../Components/TooltipModal'
import { TEXT_FIELD_STYLES, INCLUDE_LOCATIONS_VALUES, STEPS } from './constants'

const ModalFirstStep = ({
  classes,
  setValues,
  values,
  setCurrentStep,
  onClose,
  loading,
  selectedPostcodes,
  setSelectedPostcodes,
  postcodeResults,
  locationsCount,
  handleGenerate,
  isDistributionZoneExists,
  dateRange,
}) => {
  const [errors, setErrors] = useState({})
  const [postcodeSearch, setPostcodeSearch] = useState('')
  const [showPostcodeResults, setShowPostcodeResults] = useState(false)
  const [includeLocations, setIncludeLocations] = useState(
    INCLUDE_LOCATIONS_VALUES.YES,
  )
  const postcodeSearchRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        postcodeSearchRef.current &&
        !postcodeSearchRef.current.contains(event.target)
      ) {
        setShowPostcodeResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handlePostcodeSearch = e => {
    const value = e.target.value
    setPostcodeSearch(value)
  }
  const handlePostcodeInputFocus = () => {
    setShowPostcodeResults(true)
  }

  const handlePostcodeSelect = item => {
    if (
      !selectedPostcodes.some(
        selectedPostcode => selectedPostcode === item.postcode,
      )
    ) {
      setSelectedPostcodes([...selectedPostcodes, item])
    }
    setPostcodeSearch('')
    setShowPostcodeResults(false)
  }

  const handleRemovePostcode = deletedItem => {
    setSelectedPostcodes(
      selectedPostcodes.filter(item => item.postcode !== deletedItem.postcode),
    )
  }

  const validateKey = (key, value) => {
    if (
      value == null ||
      value === undefined ||
      value === '' ||
      value === 0 ||
      value === '0'
    ) {
      setErrors(prev => ({ ...prev, [key]: 'Required' }))
      return false
    } else {
      setErrors(prev => ({ ...prev, [key]: null }))
      return true
    }
  }

  const handleInputChange = (key, event) => {
    const newValue = event.target.value
    validateKey(key, newValue)
    if (['basPerMission', 'countOfMissions'].includes(key)) {
      if (newValue >= 0 && !isNaN(newValue)) {
        setValues(prev => ({
          ...prev,
          [key]: newValue,
        }))
      }
    } else {
      setValues(prev => ({
        ...prev,
        [key]: newValue,
      }))
    }
  }

  const handleDateChange = e => {
    if (e.target.value) {
      const splittesValue = e.target.value.split('-')
      const formattedValue = `${splittesValue[0].slice(0, 4)}-${
        splittesValue[1]
      }-${splittesValue[2]}`
      setValues(prev => ({
        ...prev,
        startDate: formattedValue,
      }))
    }
  }

  const handleNext = () => {
    setCurrentStep(STEPS.SECOND)
  }

  const isDisabled = useMemo(
    () =>
      !Number(values.countOfMissions) ||
      !Number(values.basPerMission) ||
      !values.startDate,
    [values.countOfMissions, values.basPerMission, values.startDate],
  )

  const filteredPostcodeResults = useMemo(
    () =>
      postcodeResults.filter(
        item =>
          item.postcode.includes(postcodeSearch) &&
          !selectedPostcodes.some(
            selectedPostcode => selectedPostcode.postcode === item.postcode,
          ),
      ),
    [postcodeSearch, postcodeResults.length, selectedPostcodes.length],
  )

  return (
    <DialogContent>
      <div className={classes.container}>
        <div className={classes.row}>
          <div className={classes.missionNumberField}>
            <div className={classes.inputLabel}>
              <span>Number of unique missions to generate</span>
              <TooltipModal content="Unique mission slots AI Roster generates for the start date's week (before duplication)." />
            </div>
            <TextField
              id="count-of-missions"
              type={'text'}
              underlineShow={false}
              style={{
                ...TEXT_FIELD_STYLES.field,
                border: errors['countOfMissions'] ? '1px solid red' : null,
              }}
              inputStyle={TEXT_FIELD_STYLES.input}
              value={values.countOfMissions}
              onChange={e => handleInputChange('countOfMissions', e)}
              onBlur={e => validateKey('countOfMissions', e.target.value)}
              error={errors['countOfMissions']}
            />
            <div className={classes.errorLabel}>
              {errors['countOfMissions']}
            </div>
          </div>

          <div className={classes.basPerMissionField}>
            <div className={classes.inputLabel}>
              <span>BAs per mission</span>
              <TooltipModal content="Number of BAs assigned to each unique mission slot. If > 1, AI duplicates unique missions." />
            </div>
            <TextField
              id="bas-per-mission"
              type={'text'}
              underlineShow={false}
              style={{
                ...TEXT_FIELD_STYLES.field,
                border: errors['basPerMission'] ? '1px solid red' : null,
              }}
              inputStyle={TEXT_FIELD_STYLES.input}
              value={values.basPerMission}
              onChange={e => handleInputChange('basPerMission', e)}
              onBlur={e => validateKey('basPerMission', e.target.value)}
              error={errors['basPerMission']}
            />
            <div className={classes.errorLabel}>{errors['basPerMission']}</div>
          </div>
          <div className={classes.startDateField}>
            <div className={classes.inputLabel}>
              <span>Missions start date</span>
              <TooltipModal content="AI Roster generates missions from this start date, and in the same week." />
            </div>
            <TextField
              id="start-date"
              type={'date'}
              underlineShow={false}
              format="DD/MM/YYYY"
              style={{
                ...TEXT_FIELD_STYLES.field,
                border: errors['startDate'] ? '1px solid red' : null,
              }}
              inputStyle={TEXT_FIELD_STYLES.dateInput}
              value={
                values.startDate
                  ? new Date(values.startDate).toISOString().split('T')[0]
                  : null
              }
              min={dateRange.minDate}
              max={dateRange.maxDate}
              onChange={handleDateChange}
              onBlur={e => validateKey('startDate', e.target.value)}
              error={errors['startDate']}
            />
            <div className={classes.errorLabel}>{errors['startDate']}</div>
          </div>
        </div>

        <div
          className={classes.zipSearchContainer}
          style={{ marginBottom: '6px' }}
        >
          <div className={classes.inputLabel}>
            <span>Target zip codes (optional)</span>
            <TooltipModal
              content={
                <div className={classes.tooltipContent}>
                  <div>
                    AI will create missions only within the specified zip codes.
                  </div>
                  <br />
                  <div>
                    <strong>What happens if I leave it blank?</strong>
                  </div>
                  <div>
                    If left blank, all locations in the city will be considered.
                  </div>
                  <br />
                  <div>
                    <strong>
                      What happens if I add more zip codes than missions?
                    </strong>
                  </div>
                  <div>
                    The system will prioritize the best performing Locations
                    within the selected zip codes.
                  </div>
                </div>
              }
            />
          </div>
          <div
            className={classes.helper}
            style={{ marginTop: '4px', marginBottom: '8px' }}
          >
            Add the ZIP codes within the city that you want to include in the
            generated missions.
          </div>
          <TextField
            id="postcode-search"
            type={'text'}
            underlineShow={false}
            style={TEXT_FIELD_STYLES.field}
            inputStyle={TEXT_FIELD_STYLES.input}
            value={postcodeSearch}
            onChange={handlePostcodeSearch}
            onFocus={handlePostcodeInputFocus}
            placeholder="Search zipcodes"
          />
          <div sty style={{ ...TEXT_FIELD_STYLES.searchIcon, top: '86px' }}>
            <SearchIcon style={TEXT_FIELD_STYLES.iconColor} />
          </div>
          {showPostcodeResults && (
            <Paper
              ref={postcodeSearchRef}
              className={classes.zipResults}
              elevation={3}
            >
              {loading ? (
                <div className={classes.placeholder}>Loading...</div>
              ) : filteredPostcodeResults.length ? (
                <List>
                  {filteredPostcodeResults.map(item => {
                    const count = item.locations.length
                    return (
                      <ListItem
                        button
                        key={item.postcode}
                        onClick={() => handlePostcodeSelect(item)}
                        style={{
                          opacity: !count ? 0.5 : 1,
                          cursor: !count ? 'not-allowed' : 'pointer',
                          pointerEvents: !count ? 'none' : 'auto',
                        }}
                      >
                        <div className={classes.listItemContainer}>
                          <div className={classes.listItemMainText}>
                            {count
                              ? `${item.postcode} - ${count} Locations`
                              : `${item.postcode} - No Locations`}
                          </div>
                        </div>
                      </ListItem>
                    )
                  })}
                </List>
              ) : (
                <div className={classes.placeholder}>No results found</div>
              )}
            </Paper>
          )}
          {!isDistributionZoneExists ? (
            <div className={classes.noDistributionZoneWrapper}>
              <div>
                Distribution Zone required for Zip Codes. Please update it for
                the City.
              </div>
              <div className={classes.limit}>
                Locations in Selected Zips: {locationsCount}
              </div>
            </div>
          ) : (
            <div className={classes.limit}>
              Locations in Selected Zips: {locationsCount}
            </div>
          )}

          <div className={classes.chipContainer}>
            {selectedPostcodes.map(item => (
              <Chip
                key={item.postcode}
                label={item.postcode}
                onDelete={() => handleRemovePostcode(item)}
                className={classes.chip}
                deleteIcon={<CloseIcon style={{ width: 16, height: 16 }} />}
                clickable={false}
              />
            ))}
          </div>
        </div>

        <div className={classes.locationSection}>
          <div className={classes.inputLabel}>
            <span>
              Would you like to include specific locations in your missions?
            </span>
            {/* <TooltipModal content="AI Roster generates missions from this start date, and in the same week." /> */}
          </div>
          <div className={classes.helper}>
            Select locations to guarantee their inclusion. If zip codes were
            selected, only locations within those zips will be available.
          </div>
          <RadioGroup
            value={includeLocations}
            onChange={e => {
              setIncludeLocations(e.target.value)
            }}
            row
          >
            <FormControlLabel
              value={INCLUDE_LOCATIONS_VALUES.YES}
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value={INCLUDE_LOCATIONS_VALUES.NO}
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
        </div>
        <div
          className={classes.modalButtonContainer}
          style={{ marginTop: selectedPostcodes.length ? '32px' : '80px' }}
        >
          <button className={classes.cancelButton} onClick={onClose}>
            CANCEL
          </button>
          {includeLocations === INCLUDE_LOCATIONS_VALUES.YES ? (
            <button
              className={
                isDisabled ? classes.disabledButton : classes.actionButton
              }
              disabled={isDisabled}
              onClick={handleNext}
            >
              NEXT
            </button>
          ) : (
            <button
              className={
                isDisabled || loading
                  ? classes.disabledButton
                  : classes.actionButton
              }
              disabled={isDisabled}
              onClick={handleGenerate}
            >
              {loading ? 'LOADING...' : 'GENERATE'}
            </button>
          )}
        </div>
      </div>
    </DialogContent>
  )
}

export default ModalFirstStep
